import { useMemo, useState } from 'react'
import { Button, Descriptions, Drawer, Select, Space, Tag, Typography } from 'antd'
import type { ColumnsType } from 'antd/es/table'
import { EnvironmentOutlined, PlusOutlined } from '@ant-design/icons'
import { useQuery } from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'
import { queryKeys } from '@/shared/api/queryKeys'
import { Can } from '@/shared/auth/Can'
import { DataTable } from '@/shared/ui/table/DataTable'
import { RowActions } from '@/shared/ui/table/RowActions'
import { OperationResultAlert } from '@/shared/ui/OperationResultAlert'
import { PageHeader } from '@/shared/ui/PageHeader'
import { useOperationRunner } from '@/shared/ui/useOperationRunner'
import { SiteFormModal } from '@/features/sites/SiteFormModal'
import {
  SITE_STATES,
  fetchSitePage,
  fetchSiteRegions,
  formatCoordinates,
  mapLink,
  setSiteState,
  type SiteEntry,
  type SiteState,
} from '@/features/sites/sitesApi'

const STATE_COLOR: Record<SiteState, string> = {
  ACTIVE: 'green',
  CLOSED: 'default',
}

/**
 * Koordinata katagi — matn + tashqi xaritaga havola.
 *
 * ⚠️ Xarita KOMPONENTI emas, HAVOLA. Loyihada birorta xarita kutubxonasi yo'q
 * va uni bitta ustun uchun qo'shish alohida qaror (`sitesApi.mapLink` izohi).
 */
function Coordinates({ site }: { site: SiteEntry }) {
  const { t } = useTranslation()
  if (site.latitude === null || site.longitude === null) {
    return <Typography.Text type="secondary">{t('common.dash')}</Typography.Text>
  }
  return (
    <Space size={6} wrap>
      <Typography.Text>{formatCoordinates(site.latitude, site.longitude)}</Typography.Text>
      <Typography.Link
        href={mapLink(site.latitude, site.longitude)}
        target="_blank"
        rel="noreferrer"
      >
        {t('sites.openMap')}
      </Typography.Link>
    </Space>
  )
}

/**
 * Joylashuvlar reyestri.
 *
 * <h2>Nima uchun bu ro'yxat bor</h2>
 * NAS ham, jihoz ham "qayerda turibdi" degan savolga javob bermaydi: birida
 * IP, ikkinchisida seriya raqami bor. Uzilish haqida qo'ng'iroq kelganda esa
 * kerak bo'ladigan narsa aynan shu — qaysi nuqta, qaysi viloyat. Reyestr shu
 * javobni BIR JOYDA saqlaydi va boshqa modullar unga id bo'yicha bog'lanadi.
 *
 * <h2>⚠️ O'CHIRISH YO'Q</h2>
 * Endpoint ham yo'q va bu ataylab (`sitesApi.ts` sarlavhasi): joylashuvga
 * platform ko'ra olmaydigan modullardan havola bo'lishi mumkin. Shuning uchun
 * bu yerda faqat "Yopish" bor — yozuv qoladi, havolalar buzilmaydi, lekin
 * yangi biriktirishlarda u belgilangan holda ko'rinadi.
 */
export function SitesPage() {
  const { t } = useTranslation()

  const [selected, setSelected] = useState<SiteEntry | null>(null)
  const [editing, setEditing] = useState<SiteEntry | null>(null)
  const [formOpen, setFormOpen] = useState(false)
  const [region, setRegion] = useState<string | null>(null)
  const [state, setState] = useState<SiteState | null>(null)

  const runner = useOperationRunner([queryKeys.sites.all])

  const regions = useQuery({
    queryKey: queryKeys.sites.regions,
    queryFn: fetchSiteRegions,
    staleTime: 5 * 60_000,
  })

  const filter = useMemo(() => ({ region, state }), [region, state])
  const fetchPage = useMemo(() => fetchSitePage(filter), [filter])

  // ⚠️ `runner` `queryKeys.sites.all` ni bekor qiladi — u PREFIKS, ya'ni
  // ro'yxat ham, nom xaritasi ham (`sites.index`) birga yangilanadi. Xarita
  // yangilanmasa NAS va jihoz jadvallari eski nomni ko'rsatib turardi.
  const changeState = (site: SiteEntry, next: SiteState) => {
    void runner.run(() => setSiteState(site.id, next)).then((result) => {
      if (result.status !== 'FAILED') setSelected({ ...site, state: next })
    })
  }

  const columns = useMemo<ColumnsType<SiteEntry>>(
    () => [
      {
        key: 'name',
        title: t('sites.name'),
        width: 220,
        render: (_, row) => <Typography.Text strong>{row.name}</Typography.Text>,
      },
      {
        key: 'region',
        title: t('sites.region'),
        width: 160,
        render: (_, row) => <Tag>{row.region}</Tag>,
      },
      {
        key: 'district',
        title: t('sites.district'),
        width: 150,
        render: (_, row) => row.district ?? t('common.dash'),
      },
      {
        key: 'address',
        title: t('sites.address'),
        render: (_, row) => row.address ?? t('common.dash'),
      },
      {
        key: 'coordinates',
        title: t('sites.coordinates'),
        width: 230,
        render: (_, row) => <Coordinates site={row} />,
      },
      {
        key: 'state',
        title: t('sites.state'),
        width: 120,
        render: (_, row) => <Tag color={STATE_COLOR[row.state]}>{t(`siteState.${row.state}`)}</Tag>,
      },
      {
        key: 'actions',
        title: '',
        width: 56,
        align: 'center',
        fixed: 'right',
        render: (_, row) => (
          <RowActions
            items={[
              { key: 'open', label: t('sites.open'), onClick: () => setSelected(row) },
            ]}
          />
        ),
      },
    ],
    [t],
  )

  return (
    <>
      <PageHeader
        title={t('nav.sites')}
        description={t('pageIntro.sites')}
        extra={
          <Can permission="site.write">
            <Button
              type="primary"
              icon={<PlusOutlined />}
              onClick={() => {
                setEditing(null)
                setFormOpen(true)
              }}
            >
              {t('sites.createTitle')}
            </Button>
          </Can>
        }
      />

      {runner.result ? (
        <div style={{ marginBottom: 16 }}>
          <OperationResultAlert
            partialNote={false}
            result={runner.result}
            closable
            onClose={runner.clear}
          />
        </div>
      ) : null}

      <Space wrap style={{ marginBottom: 16 }}>
        <Select<string>
          style={{ width: 220 }}
          value={region ?? 'ALL'}
          onChange={(value) => setRegion(value === 'ALL' ? null : value)}
          loading={regions.isPending}
          options={[
            { value: 'ALL', label: t('sites.allRegions') },
            ...(regions.data ?? []).map((value) => ({ value, label: value })),
          ]}
        />
        <Select<string>
          style={{ width: 180 }}
          value={state ?? 'ALL'}
          onChange={(value) => setState(value === 'ALL' ? null : (value as SiteState))}
          options={[
            { value: 'ALL', label: t('sites.allStates') },
            ...SITE_STATES.map((value) => ({ value, label: t(`siteState.${value}`) })),
          ]}
        />
      </Space>

      <DataTable<SiteEntry>
        queryKey={[...queryKeys.sites.all, filter]}
        fetchPage={fetchPage}
        columns={columns}
        rowKey={(row) => row.id}
        tableId="sites"
        scrollX={1100}
        stickyFirstColumn
        searchable
        searchPlaceholder={t('sites.searchPlaceholder')}
        emptyTitle={t('sites.empty')}
        emptyDescription={t('sites.emptyHint')}
        onRow={(row) => ({ style: { cursor: 'pointer' }, onClick: () => setSelected(row) })}
      />

      <Drawer
        open={selected !== null}
        onClose={() => setSelected(null)}
        width={520}
        title={selected?.name ?? ''}
        extra={
          selected ? (
            <Space>
              <Can permission="site.write">
                <Button
                  size="small"
                  onClick={() => {
                    setEditing(selected)
                    setFormOpen(true)
                  }}
                >
                  {t('sites.edit')}
                </Button>
              </Can>
              {/* ⚠️ "O'chirish" TUGMASI YO'Q va bo'lmaydi ham — endpoint yo'q
                  (`sitesApi.ts` sarlavhasi). Yopilgan yozuv reyestrda qoladi. */}
              <Can permission="site.write">
                {selected.state === 'ACTIVE' ? (
                  <Button
                    size="small"
                    danger
                    loading={runner.running}
                    onClick={() => changeState(selected, 'CLOSED')}
                  >
                    {t('sites.close')}
                  </Button>
                ) : (
                  <Button
                    size="small"
                    type="primary"
                    loading={runner.running}
                    onClick={() => changeState(selected, 'ACTIVE')}
                  >
                    {t('sites.activate')}
                  </Button>
                )}
              </Can>
            </Space>
          ) : null
        }
      >
        {selected ? (
          <>
            <Descriptions column={1} size="small" bordered>
              <Descriptions.Item label={t('sites.region')}>{selected.region}</Descriptions.Item>
              <Descriptions.Item label={t('sites.district')}>
                {selected.district ?? t('common.dash')}
              </Descriptions.Item>
              <Descriptions.Item label={t('sites.address')}>
                {selected.address ?? t('common.dash')}
              </Descriptions.Item>
              <Descriptions.Item label={t('sites.coordinates')}>
                <Coordinates site={selected} />
              </Descriptions.Item>
              <Descriptions.Item label={t('sites.state')}>
                <Tag color={STATE_COLOR[selected.state]}>{t(`siteState.${selected.state}`)}</Tag>
              </Descriptions.Item>
              <Descriptions.Item label={t('sites.notes')}>
                {selected.notes ?? t('common.dash')}
              </Descriptions.Item>
            </Descriptions>

            <Typography.Paragraph
              type="secondary"
              style={{ fontSize: 12, marginTop: 16, marginBottom: 0 }}
            >
              <EnvironmentOutlined /> {t('sites.closeHint')}
            </Typography.Paragraph>
          </>
        ) : null}
      </Drawer>

      <SiteFormModal
        open={formOpen}
        site={editing}
        onClose={() => {
          setFormOpen(false)
          // Kartochka ochiq qolsa eskirgan nusxani ko'rsatib turardi
          setSelected(null)
        }}
      />
    </>
  )
}
